import { Icon } from '@iconify/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { ExperimentDto } from '@/Domain/api/Conversation/dto/Experiment.dto';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ExclamationMarkIcon } from '@/Domain/components/icons/ExclamationMarkIcon';
import { ConversationExperimentEnum } from '@/Domain/enum/ConversationExperiment.enum';
import { ConversationExperimentStateEnum } from '@/Domain/enum/ConversationExperimentState.enum';
import { ConversationOutcomeEnum } from '@/Domain/enum/ConversationOutcome.enum';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';

const config: Record<
  ConversationOutcomeEnum,
  { result: ConversationOutcomeEnum; Icon: React.ReactNode; label: string }
> = {
  [ConversationOutcomeEnum.Resolved]: {
    result: ConversationOutcomeEnum.Resolved,
    Icon: <Icon icon="mingcute:check-line" className="size-4 p-0.5" />,
    label: 'Resolved',
  },
  [ConversationOutcomeEnum.Unresolved]: {
    result: ConversationOutcomeEnum.Unresolved,
    Icon: <ExclamationMarkIcon className="-rotate-45" />,
    label: 'Unresolved',
  },
};

type Props = {
  conversation: ConversationDto;
};

export const ConversationOutcome = ({ conversation }: Props) => {
  const { t } = useTranslation('inbox_v2');

  const outcome: ExperimentDto<ConversationOutcomeEnum> | null =
    ConversationHelper.getExperimentByKey(
      conversation.single_label_experiments,
      ConversationExperimentEnum.ConversationOutcome,
    );

  const isExperimentPending = outcome?.state === ConversationExperimentStateEnum.Pending;
  const experiment = outcome?.result ? config[outcome.result] : null;

  if (!outcome) {
    return null;
  }

  return (
    <>
      <Separator />
      <Tooltip>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between mb-2">
            <section className="flex items-center gap-1 text-foreground-subtle">
              <p className="font-semibold text-foreground-subtle">
                {t('conversation_details.outcome.title')}
              </p>
              <TooltipTrigger>
                <Icon icon="mingcute:information-line" />
              </TooltipTrigger>
              <TooltipContent className="absolute left-8 -translate-y-10 -translate-x-1/2 w-[250px]">
                {t('tooltip.outcome_description')}
              </TooltipContent>
            </section>
          </div>

          {isExperimentPending && (
            <p className="loading-text">{t('conversation_details.outcome.generating')}</p>
          )}

          {experiment && (
            <div className="flex items-center gap-2">
              <div
                className={classNames('', {
                  'bg-green-200 text-foreground-subtle rounded-full':
                    experiment.result === ConversationOutcomeEnum.Resolved,
                  'bg-red-100 text-red-500 rotate-45':
                    experiment.result === ConversationOutcomeEnum.Unresolved,
                })}
              >
                {experiment.Icon}
              </div>

              <p className="text-foreground-subtle">{experiment.label}</p>
            </div>
          )}
        </div>
      </Tooltip>
    </>
  );
};
